'use client'

/**
 * Socket.IO client for real-time communication with the mHive backend.
 *
 * Matches mSpace's channel pipeline pattern so mSpace consumers can
 * connect to mHive with the same event contract.
 *
 * Usage:
 *   import { useHiveSocket, useChannelData, useResourceUpdates } from '@/lib/socket-client'
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { io, type Socket } from 'socket.io-client'
import { getAuthTokenAsync } from '@/lib/auth'

// ---------------------------------------------------------------------------
// Types (mirrors mSpace channel-types)
// ---------------------------------------------------------------------------

export interface ChannelPagination {
  total: number
  offset: number
  limit: number
  hasMore: boolean
}

export interface ChannelDataPayload {
  channelId: string
  sourceKey: string
  data: unknown[]
  pagination: ChannelPagination
  schema?: Record<string, unknown>
}

export interface ChannelStatusPayload {
  channelId: string
  status: string
  message?: string
}

export interface ChannelSchemaPayload {
  channelId: string
  collections: Record<
    string,
    {
      description?: string
      fields: Record<string, { type: string; required?: boolean }>
      recordCount?: number
    }
  >
}

export interface ResourceUpdatePayload {
  connectionId?: string
  dataSpaceId?: string
  [key: string]: unknown
}

export interface HiveNotificationPayload {
  kind?: string                    // e.g. "service_health" | "workflow"
  event?: string                   // e.g. "down" | "recovered" | "recovery_attempted"
  service_key?: string
  service_label?: string
  severity?: 'info' | 'warn' | 'error' | 'critical'
  title?: string
  message?: string
  workflowId?: string
  workflowName?: string
  incident_id?: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Singleton socket instance
// ---------------------------------------------------------------------------

const SOCKET_URL =
  typeof window !== 'undefined'
    ? (process.env.NEXT_PUBLIC_SOCKET_URL ||
       process.env.NEXT_PUBLIC_API_URL ||
       process.env.NEXT_PUBLIC_BACKEND_URL ||
       'http://localhost:8101')
    : ''

let socket: Socket | null = null

function getSocket(): Socket {
  if (!socket && typeof window !== 'undefined') {
    socket = io(SOCKET_URL, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      // Resolved lazily on every (re)connect so the backend can put us in
      // the per-user room. Without this the default namespace can't fan
      // workflow:run:status / scatter:layout-changed / notification:new to
      // this socket.
      auth: (cb: (data: Record<string, unknown>) => void) => {
        getAuthTokenAsync()
          .then((token) => cb(token ? { token } : {}))
          .catch(() => cb({}))
      },
    })

    socket.on('connect', () => {
      console.log('[HiveSocket] Connected:', socket?.id)
    })

    socket.on('disconnect', (reason) => {
      console.log('[HiveSocket] Disconnected:', reason)
    })

    socket.on('connect_error', (err) => {
      console.warn('[HiveSocket] Connection error:', err.message)
    })
  }
  return socket!
}

export function disconnectSocket() {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

// ---------------------------------------------------------------------------
// Hook: useHiveSocket — connection status
// ---------------------------------------------------------------------------

export function useHiveSocket(): {
  connected: boolean
  socketId: string | null
} {
  const [connected, setConnected] = useState(false)
  const [socketId, setSocketId] = useState<string | null>(null)

  useEffect(() => {
    const s = getSocket()
    if (!s) return

    const onConnect = () => {
      setConnected(true)
      setSocketId(s.id ?? null)
    }
    const onDisconnect = () => {
      setConnected(false)
      setSocketId(null)
    }

    s.on('connect', onConnect)
    s.on('disconnect', onDisconnect)

    if (s.connected) {
      setConnected(true)
      setSocketId(s.id ?? null)
    }

    return () => {
      s.off('connect', onConnect)
      s.off('disconnect', onDisconnect)
    }
  }, [])

  return { connected, socketId }
}

// ---------------------------------------------------------------------------
// Hook: useChannelStatus — channel lifecycle
// ---------------------------------------------------------------------------

export function useChannelStatus(channelId: string | null): {
  status: string
  message: string | null
} {
  const [status, setStatus] = useState<string>('disconnected')
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!channelId) return
    const s = getSocket()
    if (!s) return

    s.emit('channel:join', { channelId, statusOnly: true })

    const onStatus = (payload: ChannelStatusPayload) => {
      if (payload.channelId === channelId) {
        setStatus(payload.status)
        setMessage(payload.message ?? null)
      }
    }

    s.on('channel:status', onStatus)

    return () => {
      s.off('channel:status', onStatus)
      s.emit('channel:leave', { channelId })
    }
  }, [channelId])

  return { status, message }
}

// ---------------------------------------------------------------------------
// Hook: useChannelData — live data subscription for a source key
// ---------------------------------------------------------------------------

export function useChannelData<T = Record<string, unknown>>(
  channelId: string | null,
  sourceKey: string,
): {
  data: T[]
  pagination: ChannelPagination
  schema: Record<string, unknown> | null
  loading: boolean
  error: string | null
} {
  const [data, setData] = useState<T[]>([])
  const [pagination, setPagination] = useState<ChannelPagination>({
    total: 0,
    offset: 0,
    limit: 100,
    hasMore: false,
  })
  const [schema, setSchema] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!channelId) return
    const s = getSocket()
    if (!s) return

    const onData = (payload: ChannelDataPayload) => {
      if (payload.sourceKey === sourceKey) {
        setData(payload.data as T[])
        setPagination(payload.pagination)
        if (payload.schema) setSchema(payload.schema)
        setLoading(false)
        setError(null)
      }
    }

    const onError = (payload: { channelId: string; error: string }) => {
      if (payload.channelId === channelId) {
        setError(payload.error)
        setLoading(false)
      }
    }

    s.on('channel:data', onData)
    s.on('channel:error', onError)

    return () => {
      s.off('channel:data', onData)
      s.off('channel:error', onError)
    }
  }, [channelId, sourceKey])

  return { data, pagination, schema, loading, error }
}

// ---------------------------------------------------------------------------
// Hook: useChannelRequest — request data actions
// ---------------------------------------------------------------------------

export function useChannelRequest(channelId: string | null) {
  const fetchData = useCallback(
    (sourceKey: string, params?: { limit?: number; offset?: number; filters?: Record<string, unknown> }) => {
      const s = getSocket()
      if (!s || !channelId) return
      s.emit('channel:request', { channelId, sourceKey, action: 'fetch', params })
    },
    [channelId],
  )

  const paginate = useCallback(
    (sourceKey: string, offset: number, limit?: number) => {
      const s = getSocket()
      if (!s || !channelId) return
      s.emit('channel:request', { channelId, sourceKey, action: 'paginate', params: { offset, limit } })
    },
    [channelId],
  )

  const refresh = useCallback(
    (sourceKey?: string) => {
      const s = getSocket()
      if (!s || !channelId) return
      s.emit('channel:request', { channelId, sourceKey, action: 'refresh' })
    },
    [channelId],
  )

  return { fetchData, paginate, refresh }
}

// ---------------------------------------------------------------------------
// Hook: useChannelSchema — live schema subscription
// ---------------------------------------------------------------------------

export function useChannelSchema(channelId: string | null): ChannelSchemaPayload['collections'] | null {
  const [collections, setCollections] = useState<ChannelSchemaPayload['collections'] | null>(null)

  useEffect(() => {
    if (!channelId) return
    const s = getSocket()
    if (!s) return

    const onSchema = (payload: ChannelSchemaPayload) => {
      if (payload.channelId === channelId) {
        setCollections(payload.collections)
      }
    }

    s.on('channel:schema', onSchema)
    return () => {
      s.off('channel:schema', onSchema)
    }
  }, [channelId])

  return collections
}

// ---------------------------------------------------------------------------
// Hook: useMotifStatus — real-time motif build/status updates
// ---------------------------------------------------------------------------

export interface MotifStatusPayload {
  motifId: string
  status: string
  appState?: Record<string, unknown>
}

export function useMotifStatus(
  motifId: string | null,
  onUpdate?: (payload: MotifStatusPayload) => void,
): MotifStatusPayload | null {
  const [lastUpdate, setLastUpdate] = useState<MotifStatusPayload | null>(null)
  const callbackRef = useRef(onUpdate)
  callbackRef.current = onUpdate

  useEffect(() => {
    if (!motifId) return
    const s = getSocket()
    if (!s) return

    s.emit('motif:join', { motifId })

    const handler = (payload: MotifStatusPayload) => {
      if (payload.motifId === motifId) {
        setLastUpdate(payload)
        callbackRef.current?.(payload)
      }
    }

    s.on('motif:status', handler)

    return () => {
      s.off('motif:status', handler)
      s.emit('motif:leave', { motifId })
    }
  }, [motifId])

  return lastUpdate
}

// ---------------------------------------------------------------------------
// Hook: useResourceUpdates — subscribe to connection / data-space changes
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Workflow run status — live updates for a shelf workflow card
// ---------------------------------------------------------------------------

export interface WorkflowRunStatusPayload {
  workflowId: string
  runId: string
  stepIndex?: number
  stepId?: string
  status: 'queued' | 'running' | 'ok' | 'fail' | 'cancelled'
  outputPreview?: unknown
  error?: string | null
  startedAt?: string
  finishedAt?: string
}

/**
 * Subscribe to live status updates for a single workflow card. The server
 * fans every update on `workflow:run:status` to the user's room; this
 * helper filters by `workflowId` and invokes the callback for each match.
 * Returns an unsubscribe function suitable for `useEffect` cleanup.
 */
export function subscribeWorkflowRun(
  workflowId: string,
  cb: (payload: WorkflowRunStatusPayload) => void,
): () => void {
  const s = getSocket()
  if (!s) return () => {}
  const handler = (payload: WorkflowRunStatusPayload) => {
    if (payload?.workflowId === workflowId) cb(payload)
  }
  s.on('workflow:run:status', handler)
  return () => {
    s.off('workflow:run:status', handler)
  }
}

export interface ScatterLayoutChangedPayload {
  hiveSpaceId: string
  source?: string
}

/**
 * Subscribe to server-side mutations of the user's scatter board layout.
 * Emitted by the workflow executor's `create_card` step when it appends a
 * new card to a shelf. The board listens for matches on `hiveSpaceId` and
 * re-fetches `getScatterBoardLayout` to merge in the new card.
 */
export function subscribeScatterLayoutChanged(
  hiveSpaceId: string,
  cb: (payload: ScatterLayoutChangedPayload) => void,
): () => void {
  const s = getSocket()
  if (!s) return () => {}
  const handler = (payload: ScatterLayoutChangedPayload) => {
    if (payload?.hiveSpaceId === hiveSpaceId) cb(payload)
  }
  s.on('scatter:layout-changed', handler)
  return () => {
    s.off('scatter:layout-changed', handler)
  }
}

/**
 * Subscribe to in-app notifications fanned to the current user's room.
 *
 * The backend emits ``notification:new`` from the workflow executor and
 * the service-health watchdog. Use this in a top-level layout to render
 * a toast for every system / admin alert without polling.
 *
 * Returns an unsubscribe function suitable for ``useEffect`` cleanup.
 */
export function subscribeNotifications(
  cb: (payload: HiveNotificationPayload) => void,
): () => void {
  const s = getSocket()
  if (!s) return () => {}
  const handler = (payload: HiveNotificationPayload) => {
    cb(payload)
  }
  s.on('notification:new', handler)
  return () => {
    s.off('notification:new', handler)
  }
}

export function useResourceUpdates(
  resourceType: 'connection' | 'data-space' | 'cepa',
  resourceId: string | null,
  onUpdate?: (data: ResourceUpdatePayload) => void,
): ResourceUpdatePayload | null {
  const [lastUpdate, setLastUpdate] = useState<ResourceUpdatePayload | null>(null)
  const callbackRef = useRef(onUpdate)
  callbackRef.current = onUpdate

  useEffect(() => {
    if (!resourceId) return
    const s = getSocket()
    if (!s) return

    s.emit('subscribe', { type: resourceType, id: resourceId })

    const eventName = `${resourceType}:update`
    const handler = (payload: ResourceUpdatePayload) => {
      setLastUpdate(payload)
      callbackRef.current?.(payload)
    }

    s.on(eventName, handler)

    return () => {
      s.off(eventName, handler)
      s.emit('unsubscribe', { type: resourceType, id: resourceId })
    }
  }, [resourceType, resourceId])

  return lastUpdate
}
